import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArchiveBoxIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientsAPI } from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { handleApiError, formatDate, showToast, showConfirm } from '../../utils/helpers';

const ArchivedClientsPage = () => {
  const { isAdmin } = useAuth();
  const { isRTL } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unarchivingId, setUnarchivingId] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchArchivedClients();
    }
  }, [isAdmin]);

  const fetchArchivedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientsAPI.getArchived();
      setClients(response.data.clients);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (clientId) => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من إلغاء أرشفة هذا العميل؟' : 'Are you sure you want to unarchive this client?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setUnarchivingId(clientId);
      await clientsAPI.unarchive(clientId);
      await fetchArchivedClients();
      showToast.success(isRTL ? 'تم إلغاء أرشفة العميل بنجاح!' : 'Client unarchived successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUnarchivingId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">
          {isRTL ? 'غير مصرح لك بالوصول إلى هذه الصفحة' : 'You do not have access to this page'}
        </p>
      </div>
    );
  }

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isRTL ? 'العملاء المؤرشفين' : 'Archived Clients'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isRTL 
              ? `تم العثور على ${clients.length} ${clients.length === 1 ? 'عميل مؤرشف' : 'عميل مؤرشف'}`
              : `${clients.length} ${clients.length === 1 ? 'archived client' : 'archived clients'} found`}
          </p>
        </div>
      </div>

      {error ? (
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchArchivedClients} variant="secondary">
            {isRTL ? 'إعادة المحاولة' : 'Try Again'}
          </Button>
        </div>
      ) : clients.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <ArchiveBoxIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isRTL ? 'لا توجد عملاء مؤرشفين' : 'No archived clients'}
            </h3>
            <p className="text-gray-500">
              {isRTL 
                ? 'لا توجد عملاء مؤرشفين حالياً'
                : 'No archived clients at the moment'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {clients.map((client) => (
            <Card key={client.id} className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{client.company_name}</h3>
                  {client.contact_email && (
                    <p className="text-sm text-gray-500 mb-1">{client.contact_email}</p>
                  )}
                  {client.archived_at && (
                    <p className="text-xs text-gray-400">
                      {isRTL ? 'تم الأرشفة في' : 'Archived on'} {formatDate(client.archived_at)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/clients/${client.id}`}
                    className="px-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    {isRTL ? 'عرض' : 'View'}
                  </Link>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleUnarchive(client.id)}
                    disabled={unarchivingId === client.id}
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    {isRTL ? 'إلغاء الأرشفة' : 'Unarchive'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArchivedClientsPage;

